import { find, findAll, prefersReducedMotion, require } from "./dom";

/**
 * Bildvisare för gallerierna.
 *
 * <p>Utan JavaScript är varje ruta en länk till den obeskurna bilden — det är
 * fallbacken, och den är samma sak som sajten gjorde innan visaren fanns.
 * Med JavaScript fångas klicket och bilden öppnas i ett `<dialog>` i stället
 * för att ta över fönstret.</p>
 *
 * <p>`<dialog>` med `showModal()` och inte en egen div: webbläsaren sköter
 * fokusfällan, inertheten hos sidan bakom och Escape själv. Det är mindre kod
 * och framför allt mindre kod som kan bli fel.</p>
 */
interface Slide {
  /** Obeskuren bild i full storlek — även `<a href>`, så den fungerar utan JS. */
  url: string;
  srcset: string;
  width: number;
  height: number;
  alt: string;
}

/** Kortare svep än så är ett darrigt finger, inte en gest. */
const SWIPE_THRESHOLD = 45;

interface Viewer {
  dialog: HTMLDialogElement;
  image: HTMLImageElement;
  caption: HTMLElement;
  live: HTMLElement;
}

let viewer: Viewer | null = null;
let slides: readonly Slide[] = [];
let current = 0;
let opener: HTMLElement | null = null;

function icon(path: string): string {
  return `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true"
    stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
}

function build(): Viewer {
  const dialog = document.createElement("dialog");
  dialog.className = "lightbox";
  dialog.setAttribute("aria-label", "Bildvisare");
  dialog.innerHTML = `
    <div class="lightbox__stage" data-stage>
      <img class="lightbox__img" data-image alt="" fetchpriority="high" decoding="async">
    </div>
    <button class="lightbox__close" type="button" data-close aria-label="Stäng bildvisaren">
      ${icon('<path d="M6 6l12 12M18 6L6 18"/>')}
    </button>
    <button class="lightbox__nav lightbox__nav--prev" type="button" data-prev aria-label="Föregående bild">
      ${icon('<path d="M15 5l-7 7 7 7"/>')}
    </button>
    <button class="lightbox__nav lightbox__nav--next" type="button" data-next aria-label="Nästa bild">
      ${icon('<path d="M9 5l7 7-7 7"/>')}
    </button>
    <p class="lightbox__caption" data-caption></p>
    <p class="sr-only" role="status" aria-live="polite" data-live></p>`;
  document.body.append(dialog);

  const built: Viewer = {
    dialog,
    image: require<HTMLImageElement>("[data-image]", dialog),
    caption: require("[data-caption]", dialog),
    live: require("[data-live]", dialog),
  };

  require("[data-close]", dialog).addEventListener("click", dismiss);
  require("[data-prev]", dialog).addEventListener("click", () => step(-1));
  require("[data-next]", dialog).addEventListener("click", () => step(1));

  // Klick vid sidan av bilden stänger, klick på bilden går vidare — samma
  // vana som fancybox och lightbox har lärt ut i femton år.
  dialog.addEventListener("click", (event) => {
    const target = event.target;
    if (target === dialog || (target instanceof HTMLElement && target.hasAttribute("data-stage"))) {
      dismiss();
    }
  });
  built.image.addEventListener("click", () => step(1));
  window.addEventListener("resize", () => {
    const slide = slides[current];
    if (built.dialog.open && slide !== undefined) {
      built.image.sizes = displaySize(slide);
    }
  });

  dialog.addEventListener("keydown", (event: KeyboardEvent) => {
    const keys: Record<string, () => void> = {
      ArrowLeft: () => step(-1),
      ArrowRight: () => step(1),
      Home: () => show(0),
      End: () => show(slides.length - 1),
    };
    const action = keys[event.key];
    if (action !== undefined) {
      event.preventDefault();
      action();
    }
  });

  // Svep på pekskärm. Pointer-händelser täcker både finger och penna, och
  // klicket som annars följer ett svep måste bort — annars byter bilden två
  // gånger på ett svep.
  let startX: number | null = null;
  const stage = require("[data-stage]", dialog);
  stage.addEventListener("pointerdown", (event) => {
    startX = (event as PointerEvent).clientX;
  });
  stage.addEventListener("pointerup", (event) => {
    if (startX === null) {
      return;
    }
    const dx = (event as PointerEvent).clientX - startX;
    startX = null;
    if (Math.abs(dx) >= SWIPE_THRESHOLD) {
      event.preventDefault();
      step(dx < 0 ? 1 : -1);
    }
  });

  // Escape stänger dialogen utan att gå via knapparna. Städningen ligger i en
  // egen funktion just därför, och den tål att köras två gånger.
  dialog.addEventListener("close", cleanUp);

  return built;
}

/**
 * Lämnar sidan som den var: rullningen fri, bilden släppt och fokus tillbaka
 * på rutan besökaren klickade på. Utan det sista står fokus kvar i tomma
 * intet, och den som tabbar får börja om från sidans topp.
 */
function cleanUp(): void {
  document.documentElement.style.removeProperty("overflow");
  if (viewer !== null) {
    viewer.image.removeAttribute("src");
    viewer.image.removeAttribute("srcset");
  }

  const trigger = opener;
  opener = null;
  trigger?.focus();
}

/**
 * Stänger först, städar sedan. Ordningen är inte godtycklig: en modal dialog
 * håller kvar fokus inuti sig, så fokus flyttar inte tillbaka till rutan förrän
 * dialogen faktiskt är stängd.
 */
function dismiss(): void {
  viewer?.dialog.close();
  cleanUp();
}

/** Nästa bild i tur, med varv runt — sista går till första och tvärtom. */
function step(direction: number): void {
  if (slides.length > 1) {
    show((current + direction + slides.length) % slides.length);
  }
}

function show(index: number): void {
  const slide = slides[index];
  if (viewer === null || slide === undefined) {
    return;
  }

  current = index;
  const { image, caption, live } = viewer;

  image.sizes = displaySize(slide);
  if (slide.srcset.length > 0) {
    image.srcset = slide.srcset;
  } else {
    image.removeAttribute("srcset");
  }
  image.src = slide.url;
  image.alt = slide.alt;
  if (slide.width > 0 && slide.height > 0) {
    image.width = slide.width;
    image.height = slide.height;
  }

  // Bara räknaren i bildtexten: mediebibliotekets alt-texter är för det mesta
  // kamerans filnamn ("DSC 3778"), och det är inget att visa för besökaren.
  // Alt-texten sitter kvar på bilden, där skärmläsaren hittar den.
  const position = `Bild ${index + 1} av ${slides.length}`;
  caption.textContent = position;
  live.textContent = position;

  preload(index + 1);
  preload(index - 1);
}

/** Accounts for the stage padding and the height limit on portrait photographs. */
function displaySize(slide: Slide): string {
  if (viewer === null || slide.width <= 0 || slide.height <= 0) {
    return "100vw";
  }
  const stage = require<HTMLElement>("[data-stage]", viewer.dialog);
  const style = getComputedStyle(stage);
  const width = stage.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  const height = stage.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
  return `${Math.max(1, Math.ceil(Math.min(width, height * slide.width / slide.height)))}px`;
}

/** Grannbilden hämtas i förväg så att nästa klick känns omedelbart. */
function preload(index: number): void {
  const slide = slides[(index + slides.length) % slides.length];
  if (slide === undefined) {
    return;
  }
  const image = new window.Image();
  image.fetchPriority = "low";
  image.sizes = displaySize(slide);
  if (slide.srcset.length > 0) {
    image.srcset = slide.srcset;
  }
  image.src = slide.url;
}

function open(group: readonly Slide[], index: number, trigger: HTMLElement): void {
  viewer ??= build();
  slides = group;
  opener = trigger;

  // Sidan bakom får inte rulla med. `<dialog>` gör den inert men inte stilla.
  document.documentElement.style.setProperty("overflow", "hidden");
  viewer.dialog.classList.toggle("lightbox--still", prefersReducedMotion());
  viewer.dialog.showModal();
  show(index);
}

/**
 * Samlar rutorna till grupper.
 *
 * <p>Samma bild förekommer i både rutnätet överst och galleriet längre ned.
 * `data-lightbox-index` är platsen i galleriet, och dubbletterna faller bort
 * på det — annars hade visaren visat de första bilderna två gånger.</p>
 */
export function initLightbox(): void {
  const groups = new Map<string, Map<number, Slide>>();

  for (const link of findAll<HTMLAnchorElement>("a[data-lightbox]")) {
    const name = link.dataset["lightbox"] ?? "";
    const index = Number(link.dataset["lightboxIndex"] ?? "0");
    const image = find<HTMLImageElement>("img", link);

    const group = groups.get(name) ?? new Map<number, Slide>();
    if (!group.has(index)) {
      group.set(index, {
        url: link.href,
        srcset: link.dataset["lightboxSrcset"] ?? "",
        width: Number(link.dataset["lightboxWidth"] ?? "0"),
        height: Number(link.dataset["lightboxHeight"] ?? "0"),
        alt: image?.alt ?? find<HTMLElement>("[data-tile-alt]", link)?.dataset.tileAlt ?? "",
      });
    }
    groups.set(name, group);
  }

  for (const link of findAll<HTMLAnchorElement>("a[data-lightbox]")) {
    const name = link.dataset["lightbox"] ?? "";
    const group = groups.get(name);
    if (group === undefined) {
      continue;
    }

    const ordered = [...group.entries()].sort((a, b) => a[0] - b[0]);
    const slideList = ordered.map(([, slide]) => slide);
    const position = ordered.findIndex(([index]) => index === Number(link.dataset["lightboxIndex"] ?? "0"));

    link.addEventListener("click", (event: MouseEvent) => {
      // Låt webbläsaren göra sitt när besökaren vill öppna i ny flik.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      open(slideList, position < 0 ? 0 : position, link);
    });
  }
}
